import { useEffect, useState } from 'react';
import { DASHBOARD_VIEWS, type DashboardView } from '@gc/domain';

/**
 * The fleet at a glance. Numbers first, because the question a fleet manager opens this to ask
 * is "what needs attention" — not "show me a chart".
 */

export interface DashboardSummary {
  totalGadgets: number;
  byStatus: { key: string; count: number }[];
  byCategory: { key: string; count: number }[];
  warranty: { expired: number; within30: number; within60: number; within90: number; covered: number; unknown: number };
  openTicketsByStatus: { key: string; count: number }[];
  repairSpend: { month: string; minor: number; repairs: number }[];
  ageing: { id: string; assetTag: string; make: string; model: string; purchasedOn: string | null; ageDays: number | null }[];
  recentlyAdded: { id: string; assetTag: string; make: string; model: string; status: string; at: string }[];
  recentlyChanged: { id: string; assetTag: string; make: string; model: string; status: string; at: string }[];
}

export interface CsvFile {
  filename: string;
  csv: string;
}

export interface DashboardApi {
  getDashboard: () => Promise<DashboardSummary>;
  exportCsv: (what: 'gadgets' | 'repairs' | `dashboard/${DashboardView}`) => Promise<CsvFile>;
}

const LABELS: Record<string, string> = {
  procured: 'Procured', in_stock: 'In stock', assigned: 'Assigned', in_service: 'In service',
  under_repair: 'Under repair', awaiting_parts: 'Awaiting parts', returned: 'Returned',
  retired: 'Retired', disposed: 'Disposed',
  raised: 'Raised', triaged: 'Triaged', dispatched: 'Dispatched', in_repair: 'In repair',
};
const label = (k: string) => LABELS[k] ?? k;

/** Prices are stored in paise; nobody wants to read a fleet's spend in paise. */
const rupees = (minor: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(minor / 100);

type Load = { kind: 'loading' } | { kind: 'ready'; data: DashboardSummary } | { kind: 'error'; message: string };

export function Dashboard({ api, canExport }: { api: DashboardApi; canExport: boolean }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [view, setView] = useState<DashboardView>('status');
  const [failure, setFailure] = useState<string | null>(null);

  /** Fetches the file with the caller's token, then hands it to the browser to save. */
  async function download(what: Parameters<DashboardApi['exportCsv']>[0]) {
    setBusy(what);
    setFailure(null);
    try {
      const file = await api.exportCsv(what);
      const url = URL.createObjectURL(new Blob([file.csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      // Revoking immediately would cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'export failed');
    } finally {
      setBusy(null);
    }
  }

  useEffect(() => {
    let live = true;
    api.getDashboard()
      .then((data) => live && setLoad({ kind: 'ready', data }))
      .catch((e: unknown) => live && setLoad({
        kind: 'error', message: e instanceof Error ? e.message : 'unknown error',
      }));
    return () => { live = false; };
  }, [api]);

  if (load.kind === 'loading') return <p>Loading the dashboard…</p>;
  if (load.kind === 'error') return <p data-testid="dashboard-error">Could not load the dashboard: {load.message}</p>;

  const d = load.data;
  const needsAttention = d.warranty.expired + d.warranty.within30;
  const openRepairs = d.openTicketsByStatus.reduce((n, t) => n + t.count, 0);

  return (
    <section>
      <h2>Fleet</h2>

      {/* The four numbers someone opens this to read, before any breakdown. */}
      <div className="stats">
        <div className="stat">
          <div className="stat-label">Gadgets</div>
          <div className="stat-value" data-testid="total">{d.totalGadgets}</div>
        </div>
        {needsAttention > 0 && (
          <div className="stat attention">
            <div className="stat-label">Need attention</div>
            <div className="stat-value" data-testid="attention">{needsAttention}</div>
          </div>
        )}
        <div className="stat">
          <div className="stat-label">Open repairs</div>
          <div className="stat-value">{openRepairs}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Out of warranty</div>
          <div className="stat-value">{d.warranty.expired}</div>
        </div>
      </div>

      <h3>Warranty</h3>
      <ul>
        <li data-testid="warranty-expired">Out of warranty: <strong>{d.warranty.expired}</strong></li>
        <li>Expiring within 30 days: <strong>{d.warranty.within30}</strong></li>
        <li>Within 60 days: <strong>{d.warranty.within60}</strong></li>
        <li>Within 90 days: <strong>{d.warranty.within90}</strong></li>
        <li>Covered beyond 90 days: {d.warranty.covered}</li>
        <li>No warranty date recorded: {d.warranty.unknown}</li>
      </ul>

      {/* Three breakdowns, side by side when the viewport allows. Reading them as one row is
          the point: status, kind, and what is in for repair answer one question together. */}
      <div className="cols">
        <div>
          <h3>By status</h3>
          {d.byStatus.length === 0 ? <p>Nothing yet.</p> : (
            <ul>
              {d.byStatus.map((s) => (
                <li key={s.key}>
                  <span className={`pill ${s.key}`}>{label(s.key)}</span>
                  <strong>{s.count}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h3>By category</h3>
          {d.byCategory.length === 0 ? <p>Nothing yet.</p> : (
            <ul>{d.byCategory.map((c) => <li key={c.key}>{c.key}<strong>{c.count}</strong></li>)}</ul>
          )}
        </div>

        <div>
          <h3>Open repairs</h3>
          {d.openTicketsByStatus.length === 0
            ? <p data-testid="no-repairs">Nothing is in for repair.</p>
            : (
              <ul>
                {d.openTicketsByStatus.map((t) => (
                  <li key={t.key}>
                    <span className={`pill ${t.key}`}>{label(t.key)}</span>
                    <strong>{t.count}</strong>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>

      <h3>Repair spend</h3>
      {d.repairSpend.length === 0 ? <p>No completed repairs yet.</p> : (
        <div className="scroll-x"><table>
          <thead><tr><th scope="col">Month</th><th scope="col">Repairs</th><th scope="col">Spend</th></tr></thead>
          <tbody>
            {d.repairSpend.map((s) => (
              <tr key={s.month}><td>{s.month}</td><td>{s.repairs}</td><td>{rupees(s.minor)}</td></tr>
            ))}
          </tbody>
        </table></div>
      )}

      <h3>Oldest still in service</h3>
      {d.ageing.length === 0 ? <p>Nothing with a purchase date recorded.</p> : (
        <ul>
          {d.ageing.map((g) => (
            <li key={g.id}>
              {g.assetTag} — {g.make} {g.model}
              {g.ageDays !== null && <> · {Math.floor(g.ageDays / 365)} years old</>}
            </li>
          ))}
        </ul>
      )}

      <h3>Recently added</h3>
      <ul>{d.recentlyAdded.map((g) => <li key={g.id}>{g.assetTag} — {g.make} {g.model}</li>)}</ul>

      <h3>Recently changed</h3>
      <ul>
        {d.recentlyChanged.map((g) => (
          <li key={g.id}>{g.assetTag}<span className={`pill ${g.status}`}>{label(g.status)}</span></li>
        ))}
      </ul>

      {/* The API enforces data:export independently; hiding these is a courtesy.
          They are buttons rather than links because the export needs an Authorization header,
          which a plain href cannot send — so the file is fetched and handed to the browser. */}
      {canExport && (
        <p>
          Export:{' '}
          <button type="button" className="primary" data-testid="export-gadgets" disabled={busy !== null}
            onClick={() => void download('gadgets')}>
            {busy === 'gadgets' ? 'Preparing…' : 'gadgets CSV'}
          </button>
          {' · '}
          <button type="button" data-testid="export-repairs" disabled={busy !== null}
            onClick={() => void download('repairs')}>
            {busy === 'repairs' ? 'Preparing…' : 'repairs CSV'}
          </button>
          {' · '}
          <select aria-label="Dashboard view to export" value={view} data-testid="export-view"
            onChange={(e) => setView(e.target.value as DashboardView)}>
            {DASHBOARD_VIEWS.map((v) => <option key={v} value={v}>{v.replace(/-/g, ' ')}</option>)}
          </select>{' '}
          <button type="button" data-testid="export-view-download" disabled={busy !== null}
            onClick={() => void download(`dashboard/${view}`)}>
            {busy === `dashboard/${view}` ? 'Preparing…' : 'view CSV'}
          </button>
          {failure && <span role="alert" data-testid="export-error"> {failure}</span>}
        </p>
      )}
    </section>
  );
}
